<template>
    <b-modal
        id="dlgTask"
        ref="taskDialog"
        :title="dialogTitle"
        :ok-title="saveButtonTitle"
        :cancel-title="$t('general.cancel')"
        cancel-variant="secondary"
        header-bg-variant="secondary"
        header-text-variant="white"
        centered
        no-fade
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        @ok.prevent="onOkClicked"
        @shown="onShown"
        >
        <b-form-group v-if="inputLimits">
            <template slot="label">
                <b-row>
                    <b-col cols="9">{{ $t('tasks.subject') }}: *</b-col>
                    <b-col cols="3" class="text-right">
                        <small>
                            <b-badge>{{task.subject.length}} / {{inputLimits.taskName}}</b-badge>
                        </small>
                    </b-col>
                </b-row>
            </template>
            <b-form-input
                id="txtNewTaskSubject"
                name="newTaskSubject"
                ref="newTaskSubjectInput"
                type="text"
                :maxlength="inputLimits.taskName"
                v-model="task.subject"
                v-validate="'required'"
                :state="!veeErrors.has('newTaskSubject')"
                @keyup.enter.native="onOkClicked">
            </b-form-input>
            <span v-show="veeErrors.has('newTaskSubject')" style="color: red;" class="small">{{ $t('tasks.messages.validation.taskName') }}</span>
        </b-form-group>
        <b-form-group v-if="inputLimits">
            <template slot="label">
                <b-row>
                    <b-col cols="9">{{ $t('tasks.description') }}:</b-col>
                    <b-col cols="3" class="text-right">
                        <small>
                            <b-badge>{{task.description.length}} / {{inputLimits.taskDescription}}</b-badge>
                        </small>
                    </b-col>
                </b-row>
            </template>
            <b-form-textarea name="newTaskDescription"
                             ref="newTaskDescription"
                             v-model="task.description"
                             :maxlength="inputLimits.taskDescription"
                             rows="3" max-rows="3" no-resize>
            </b-form-textarea>
        </b-form-group>
        <b-form-group>
            <label>{{ $t('tasks.dueDate') }}:</label>
            <datepicker
                bootstrap-styling
                monday-first
                v-model="task.due"
                format="dd.MM.yyyy"
                :language="languages[currentUserLanguage]">
            </datepicker>
        </b-form-group>
        <b-form-group>
            <b-row align-v="center">
                <b-col cols="8">
                    <label class="mb-0">{{ $t('tasks.assignProjectMember') }}:</label>
                </b-col>
                <b-col cols="4" class="text-right">
                    <b-btn @click="removeMember()" :disabled="selectedMember === null || !projectPermissions.includes('DELETE_MEMBERSHIP')">
                        <small>{{ $t('tasks.removeAssignee') }}</small>
                    </b-btn>
                </b-col>
            </b-row>
            <v-autocomplete ref="autocompleteTaskAssignee"
                :items="projectCollaborators"
                v-model="selectedMember"
                :get-label="getLabel"
                :auto-select-one-item="false"
                :input-attrs="inputAttributes"
                :component-item="itemTemplate"
                @change="onInputChange"
                @update-items="filter">
            </v-autocomplete>
        </b-form-group>
    </b-modal>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import _ from 'lodash';
import toastr from 'toastr';
import moment from 'moment';
import i18n from '@/i18n.bootstrap';
import Datepicker from 'vuejs-datepicker';
import * as datePickerLanguages from 'vuejs-datepicker/src/locale';
import ProjectMembersDialogTpl from './ProjectMembersDialogTpl';

export default {
    name: 'task-dialog',

    props: ['taskListId'],

    components: { Datepicker },

    data() {
        return {
            dialogTitle: '',
            saveButtonTitle: '',

            task: {
                subject: '',
                description: '',
                assignedTo: ''
            },
            isNewTask: true,

            languages: datePickerLanguages,

            projectCollaborators: [],
            selectedMember: null,
            itemTemplate: ProjectMembersDialogTpl,
            inputAttributes: {
                class: 'form-control',
                placeholder: i18n.t('tasks.startTyping')
            }
        }
    },

    mounted() {
        this.$root.$on('project-tasks::create-task', () => {
            this.task = {
                subject: '',
                description: '',
                assignedTo: ''
            };
            this.selectedMember = null;
            this.isNewTask = true;
            this.dialogTitle = i18n.t('tasks.newTask');
            this.saveButtonTitle = i18n.t('tasks.addTask');
        });

        this.$root.$on('project-tasks::edit-task', (payload) => {
            this.task = _.clone(payload.task);
            this.selectedMember = this.task.assignedTo === '' ? null : this.getProjectMember();
            this.isNewTask = false;
            this.dialogTitle = i18n.t('tasks.editTask');
            this.saveButtonTitle = i18n.t('tasks.saveChanges');
        });
    },

    computed: {
        ...mapState('system', ['inputLimits']),
        ...mapState('userData', {currentUserLanguage: 'language'}),
        ...mapGetters({projectMembers: 'projects/projectCollaborators'}),

        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
    },

    watch: {
        projectMembers(newArray) {
            this.projectCollaborators = newArray;
        }
    },

    methods: {
        removeMember() {
            this.selectedMember = null; 
            this.onInputChange();
            this.$refs.autocompleteTaskAssignee.$el.children[0].children[0].focus();
        },

        onInputChange(text) {
            if (!text || text === '') {
                this.projectCollaborators = _.cloneDeep(this.projectMembers);
            }
        },

        filter(searchText) {
            if (searchText && searchText !== '') {
                this.projectCollaborators = _.cloneDeep(this.projectMembers).filter(item => {
                    return (new RegExp(searchText.toLowerCase())).test(item['displayName'].toLowerCase());
                });
            }
        },

        getLabel(item) {
            if(item) return item.displayName;
        },

        getProjectMember() {
            return this.projectCollaborators.find(m => m.id === this.task.assignedTo) || {};
        },

        onOkClicked(event) {
            this.$validator.validateAll()
                .then(result => {
                    if(result) {

                        // prevents the modal from closing
                        event.preventDefault();

                        if (moment(moment().format('YYYY-MM-DD')).isAfter(moment(moment(this.task.due).format('YYYY-MM-DD')))) {
                            toastr.warning(i18n.t('tasks.messages.validation.taskDueDate'));
                            return;
                        }

                        this.task.assignedTo = this.selectedMember ? this.selectedMember.id : '';

                        if (this.isNewTask) {
                            this.$store.dispatch('projects/addTask', {
                                listId: this.taskListId,
                                task: this.task
                            }).then(() => {
                                this.$root.$emit('bv::hide::modal', 'dlgTask');
                            });
                        } else {
                            // current date & time formatted as ISO8601
                            this.task.modified = moment().format();
                            this.$store.dispatch('projects/editTask', {
                                listId: this.taskListId,
                                task: this.task
                            }).then(() => {
                                this.$root.$emit('bv::hide::modal', 'dlgTask');
                            });
                        }
                    }
                });
        },

        onShown() {

        }
    }
}
</script>

<style>
</style>
